import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface DeletePropertyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  propertyTitle: string;
  isBulkAction?: boolean;
}

const DeletePropertyModal: React.FC<DeletePropertyModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  propertyTitle,
  isBulkAction = false
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const requiredText = 'EXCLUIR';

  const handleConfirm = () => {
    if (confirmationText === requiredText) {
      onConfirm();
      setConfirmationText('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[85vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {isBulkAction ? 'Excluir Imóveis' : 'Excluir Imóvel'}
              </h2>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-800 mb-1 text-sm">
                  ⚠️ AÇÃO IRREVERSÍVEL
                </h3>
                <p className="text-red-700 text-xs">
                  {isBulkAction 
                    ? 'Esta ação irá excluir permanentemente os imóveis selecionados do sistema.'
                    : 'Esta ação irá excluir permanentemente o imóvel do sistema.'
                  }
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2 text-sm">
              {isBulkAction ? 'Imóveis a serem excluídos:' : 'Imóvel a ser excluído:'}
            </h4>
            <div className="bg-gray-50 p-2 rounded border">
              <p className="text-xs text-gray-700 font-medium">
                {propertyTitle}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Para confirmar, digite "<span className="font-bold text-red-600">EXCLUIR</span>":
            </label>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
              placeholder="Digite EXCLUIR para confirmar"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-3 h-3 text-yellow-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-yellow-800 text-xs">
                  <strong>Alternativa:</strong> Use "<strong>Inativar</strong>" ao invés de excluir.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmationText !== requiredText}
            className={`px-4 py-2 rounded-md transition-colors ${
              confirmationText === requiredText
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isBulkAction ? 'Excluir Imóveis' : 'Excluir Imóvel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeletePropertyModal;